import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  useCreateChatMutation,
  useNotifyTeamOwnerMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';

interface TeamExpiryBannerProps {
  onUpgradeNow: () => void;
  isOwnerOrAdmin?: boolean;
  expiredDate?: Date;
  planName: string;
  teamId?: string;
  teamOwnerId?: string;
  teamOwnerName?: string;
}

export const TeamExpiryBanner: React.FC<TeamExpiryBannerProps> = ({
  onUpgradeNow,
  isOwnerOrAdmin,
  expiredDate,
  planName,
  teamId,
  teamOwnerId,
  teamOwnerName,
}) => {
  const { t } = useTranslation('models');
  const toast = useToast();
  const { navigateToChatDrawer: navigateTo } = useAppNavigation();
  const { chatExpanded, chatListOpen, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();
  const { setActiveMessageId, setIsOpenChat } = useActiveChat();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    navigateTo({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  const [notifyTeamOwner, { loading: notifyLoading, data: notifyTeamOwnerData, error: notifyTeamOwnerError }] =
    useNotifyTeamOwnerMutation();

  useEffect(() => {
    if (!notifyTeamOwnerData) return;
    if (teamOwnerId) {
      createChat({
        variables: {
          attributes: { name: teamOwnerName ?? '', userIds: [teamOwnerId] },
        },
      });
    }
  }, [notifyTeamOwnerData]);

  useEffect(() => {
    if (!notifyTeamOwnerError) return;
    const code = notifyTeamOwnerError.graphQLErrors?.[0]?.extensions?.code as
      | string
      | undefined;
    let message = t('models:subscription.notifyOwner.failed');
    if (code === 'RATE_LIMITED') {
      message = t('models:subscription.notifyOwner.rateLimited');
    } else if (code === 'NO_RECIPIENTS') {
      message = t('models:subscription.notifyOwner.noRecipients');
    }
    toast.show(message, { type: 'danger', duration: 4000 });
  }, [notifyTeamOwnerError]);

  const handleNotifyOwner = () => {
    if (teamId) {
      notifyTeamOwner({ variables: { teamId } });
    }
  };

  const expiredDateStr = () => {
    if (expiredDate && expiredDate < new Date()) {
      if (dayjs(expiredDate).year() === dayjs().year()) {
        return 'ended on ' + dayjs(expiredDate).format('MMMM D');
      } else {
        return 'ended on ' + dayjs(expiredDate).format('MMMM D, YYYY');
      }
    }
    return 'ended';
  };

  return (
    <>
      <Box style={styles.container}>
        <Box style={styles.leftContent}>
          <Box style={styles.iconContainer}>
            <Icon name='AlertTriangle' height={24} width={24} />
          </Box>
          <Box style={styles.textContainer}>
            <Text variant='labelLarge'>
              Access to Team Plan for {planName} has ended
            </Text>
            <Text variant='webBodySecondary' color='textSecondary'>
              Your access {expiredDateStr()}. Upgrade to a Team plan to use
              advanced project features.
            </Text>
          </Box>
        </Box>
        <Box style={styles.buttonContainer}>
          {isOwnerOrAdmin ? (
            <Button
              isSmall
              color='white'
              disabled={false}
              backgroundColor='black'
              variant='webprimary'
              accessibilityLabel='Upgrade'
              onPress={onUpgradeNow}>
              Upgrade Now
            </Button>
          ) : (
            !!teamId && (
              <Button
                isSmall
                height={32}
                disabled={notifyLoading}
                backgroundColor='black'
                variant='webprimary'
                paddingHorizontal='m'
                accessibilityLabel='Notify Owner'
                onPress={handleNotifyOwner}>
                {notifyLoading ? 'Notifying...' : 'Notify Owner'}
              </Button>
            )
          )}
        </Box>
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.yellowVividMild,
    borderRadius: theme.spacing.s,
    padding: theme.spacing.m,
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: theme.colors.yellowVivid,
    justifyContent: 'space-between',
    alignContent: 'center',
    alignItems: 'center',
    marginHorizontal: theme.spacing.m,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    flexWrap: 'wrap',
  },
  leftContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  iconContainer: {
    marginRight: theme.spacing.s,
    flexShrink: 0,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: theme.spacing.m,
    flexShrink: 0,
  },
});
